/** Status rollups and a status-only CSV. Extra answer fields on a person are ignored. */

export const STATUS_LABELS = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

const MODULE_KEYS = ['A', 'B', 'C', 'D'];

function csvCell(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function rollup(person, moduleKey) {
  const assigned = Array.isArray(person?.assignedModules) ? person.assignedModules : [];
  const keys = moduleKey ? assigned.filter((key) => key === moduleKey) : assigned.slice();
  if (keys.length === 0) return null;
  const rows = keys.map((key) => person.modules?.[key]).filter(Boolean);
  if (rows.length === 0) return null;
  const allComplete = rows.every((row) => row.status === 'complete');
  const allNotStarted = rows.every((row) => row.status === 'not_started');
  const status = allComplete ? 'complete' : allNotStarted ? 'not_started' : 'in_progress';
  const answered = rows.reduce((sum, row) => sum + (row.answered || 0), 0);
  const total = rows.reduce((sum, row) => sum + (row.total || 0), 0);
  return {
    status,
    statusLabel: STATUS_LABELS[status],
    answered,
    total,
    percent: total ? Math.round((answered / total) * 100) : 0,
    modules: keys,
  };
}

export function filterPeople(people, { moduleKey = '', status = '', query = '' } = {}) {
  const needle = query.trim().toLowerCase();
  return people
    .map((person) => ({ ...person, rollup: rollup(person, moduleKey || undefined) }))
    .filter((person) => person.rollup)
    .filter((person) => !status || person.rollup.status === status)
    .filter((person) => {
      if (!needle) return true;
      return `${person.name} ${person.email}`.toLowerCase().includes(needle);
    });
}

export function dashboardTotals(people) {
  const tasked = people.length;
  const notStarted = people.filter((person) => person.rollup?.status === 'not_started').length;
  const inProgress = people.filter((person) => person.rollup?.status === 'in_progress').length;
  const completed = people.filter((person) => person.rollup?.status === 'complete').length;
  const answered = people.reduce((sum, person) => sum + (person.rollup?.answered || 0), 0);
  const total = people.reduce((sum, person) => sum + (person.rollup?.total || 0), 0);
  return {
    tasked,
    notStarted,
    inProgress,
    completed,
    percent: total ? Math.round((answered / total) * 100) : 0,
  };
}

export function activityDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export function buildProgressCsv(people) {
  const headers = [
    'Name',
    'Email',
    'Assigned modules',
    'Status',
    'Percent answered',
    'A status',
    'A percent',
    'B status',
    'B percent',
    'C status',
    'C percent',
    'D status',
    'D percent',
    'Last activity',
  ];
  const lines = [headers.join(',')];
  people.forEach((person) => {
    const summary = person.rollup || rollup(person);
    if (!summary) return;
    const moduleCells = MODULE_KEYS.flatMap((key) => {
      const row = person.modules?.[key];
      const included = summary.modules.includes(key) && row;
      return [
        included ? STATUS_LABELS[row.status] || '' : '',
        included ? String(row.percent || 0) : '',
      ];
    });
    lines.push([
      person.name || '',
      person.email || '',
      summary.modules.join(' '),
      summary.statusLabel,
      String(summary.percent),
      ...moduleCells,
      activityDate(person.lastActivityAt),
    ].map(csvCell).join(','));
  });
  return `${lines.join('\n')}\n`;
}
